<template>
  <div class="clinicView">
    <vs-card id="editPage">

      <div slot="header" class="flex items-center justify-end space-x-2 w-full">
        <vs-button @click="getAccountLogs" class="flex-1 lg:flex-none bg-green-light text-primary hover:text-primary" type="filled"> View Logs </vs-button>
        <vs-button @click="saveData" icon-pack="feather" class="flex-1 lg:flex-none">Save Details</vs-button>
      </div>

      <h5>Organization Details</h5>
      <div class="w-full lg:w-1/2 my-5 flex flex-col lg:flex-row flex-no-wrap items-end space-x-4">
          <vs-input
            :success="
              !errors.first('basic.name') && organizationDetail.name != ''
            "
            val-icon-success="done"
            val-icon-danger="error"
            :danger="errors.first('basic.name') ? true : false"
            v-validate="'required'"
            name="organizationName"
            :danger-text="errors.first('basic.name')"
            data-vv-as="ABN"
            label="Organization Name"
            class="w-full"
            data-vv-scope="basic"
            v-model="organizationDetail.name"
            disabled
          />
        <div class="w-full lg:w-auto flex items-center space-x-2 pb-2">
            <change-org
              :clinicId="clinicId"
              :clinicData="clinicData"
              :currentOrg="organizationDetail._id"
              :updater="updater"
              @refetchClinic="getClinicDetail"
            />
            <vs-button
              type="flat"
              icon-pack="feather"
              icon="icon-external-link"
              icon-after
              @click="openOrganization"
            >
              View
            </vs-button>
        </div>
      </div>

      <vs-divider class="my-8"></vs-divider>
      
      <div v-if="$isAuRegion()">
        <h5>Compliance Model</h5>
  
        <div class="grid grid-cols lg:grid-cols-2 gap-x-8 gap-y-4 mt-5">
          <vs-select
            name="complianceModel"
            v-model="selectedModel"
            class="w-full"
            label="Compliance Model"
            autocomplete>
            <vs-select-item v-for="(item, index) in models" :key="index" :value="item.value" :text="item.text" />
  
          </vs-select>
        </div>
  
        <div v-if="selectedModel === 'Model 1' || selectedModel === 'Model 2'">
          <div class="mt-10"
            :class="{'mb-5': clinicData.doctorApprovalFlowEnabled, 'mb-10': !clinicData.doctorApprovalFlowEnabled}">
            <label class="font-bold">Doctor Approval Flow</label>
            <div class="flex" >
              <div class="mt-5">
                <label>Enable Doctor Approval Flow</label>
                <vs-switch v-model="clinicData.doctorApprovalFlowEnabled" />
              </div>
            </div>
          </div>
          <div class="grid grid-cols lg:grid-cols-2 gap-x-8 gap-y-4 mt-5" v-if="clinicData.doctorApprovalFlowEnabled">
            <vs-select
              :danger="errors.first('basic.oversightDoctors') ? true : false"
              :danger-text="errors.first('basic.oversightDoctors')"
              :success="!errors.first('basic.oversightDoctors') && clinicData.oversightDoctors != ''"
              data-vv-scope="basic"
              val-icon-success="done"
              val-icon-danger="error"
              data-vv-as="Oversight Doctor"
              v-validate="`required`"
              name="oversightDoctors"
              v-model="selected"
              class="w-full"
              label="Oversight Practitioner"
              autocomplete>
              <vs-select-item v-for="(item, index) in doctors" :key="index" :value="item._id" :text="item.name" />
            </vs-select>
          </div>
        </div>
  
        <vs-divider class="my-8"></vs-divider>
      </div>

      <h5>Clinic Details</h5>

      <div class="grid grid-cols lg:grid-cols-2 gap-x-8 gap-y-4 mt-5">
        <div class="w-full">
          <vs-input
            :success="
              !errors.first('basic.clinicName') && clinicData.clinicName != ''
            "
            val-icon-success="done"
            val-icon-danger="error"
            :danger="errors.first('basic.clinicName') ? true : false"
            v-validate="'required'"
            name="clinicName"
            :danger-text="errors.first('basic.clinicName')"
            data-vv-as="clinic name"
            label="Clinic Name"
            class="w-full"
            data-vv-scope="basic"
            v-model="clinicData.clinicName"
          />
        </div>

        <div class="w-full">
          <vs-input
            :danger="errors.first('bestContactNumber') ? true : false"
            :success="
              !errors.first('bestContactNumber') && clinicData.bestContactNumber != ''
            "
            val-icon-success="done"
            val-icon-danger="error"
            name="phoneNumber"
            label="Phone Number (Optional)"
            v-model="clinicData.bestContactNumber"
            class="w-full"
          />
          <span class="text-danger text-sm error-text">{{
            errors.first("bestContactNumber")
          }}</span>
        </div>

        <div class="w-full">
          <label>Clinic Address</label>
          <div id="geocoder"></div>
        </div>

        <div class="w-full">
          <vs-input
            :success="
              !errors.first('basic.city') &&
              clinicData.address && clinicData.address.suburb != ''
            "
            val-icon-success="done"
            val-icon-danger="error"
            :danger="errors.first('basic.city') ? true : false"
            name="city"
            data-vv-scope="basic"
            :danger-text="errors.first('basic.city')"
            data-vv-as="City"
            label="City"
            class="w-full"
            v-model="clinicData.address.suburb"
          />
        </div>

        <div class="w-full">
          <vs-select
            v-validate="'required'"
            data-vv-as="visible to"
            name="state"
            placeholder="State"
            autocomplete
            v-model="clinicData.address.state" 
            class="w-full"
            val-icon-success="done"
            val-icon-danger="error"
            :danger="errors.first('basic.state') ? true : false"
            :danger-text="errors.first('basic.state')"
            data-vv-scope="basic"
            label="State"
            :success="
              !errors.first('basic.state') && clinicData.address.state != ''
            "
          >
            <vs-select-item
              :key="index"
              :value="item.name"
              :text="item.name"
              v-for="(item, index) in stateOptions"
              :clearable="false"
            />
          </vs-select>
        </div>
        <div class="w-full">
          <vs-input
            :success="
              !errors.first('basic.postCode') &&
              clinicData.address && clinicData.address.postCode != ''
            "
            val-icon-success="done"
            val-icon-danger="error"
            :danger="errors.first('basic.postCode') ? true : false"
            name="postCode"
            data-vv-scope="basic"
            :danger-text="errors.first('basic.postCode')"
            data-vv-as="postCode"
            label="Zipcode"
            class="w-full"
            v-model="clinicData.address.postCode"
          />
        </div>

        <div class="w-full">
          <vs-input
            :success="
              !errors.first('basic.postalAddress') &&
              clinicData.address && clinicData.postalAddress != ''
            "
            val-icon-success="done"
            val-icon-danger="error"
            :danger="errors.first('basic.postalAddress') ? true : false"
            name="postalAddress"
            data-vv-scope="basic"
            :danger-text="errors.first('basic.postalAddress')"
            data-vv-as="postal address"
            label="Clinic Postal Address (Optional)"
            class="w-full"
            v-model="clinicData.postalAddress"
          />
        </div>

        <div class="w-full">
          <vs-select
            id="clinic_status"
            v-model="selectedStatus"
            class="w-full"
            label="Clinic Status"
            autocomplete
            @input="onStatusChange"
          >
            <vs-select-item
              v-for="option in clinicStatusOptions"
              :key="option.value"
              :value="option.value"
              :text="option.text"
            />
          </vs-select>
          <span
            v-if="showReasonRequiredForStatus"
            class="ml-1 text-xs text-primary cursor-pointer underline inline-block mt-1"
            @click="toggleChurnSidebar"
          >
            Set / Change Churn Reason
          </span>
        </div>

        <!-- Churn Reason Drawer -->
        <vs-sidebar position-right parent="body" class="sidebarx" spacer click-not-close v-model="churnSidebarActive">
          <div class="p-6 flex flex-col gap-4">
            <div class="flex items-start justify-between">
              <h4 class="text-xl mb-1">Set {{ reasonSidebarStatusLabel }} - Reason Required</h4>
              <vs-button icon-pack="feather" icon="icon-x" type="flat" @click="cancelChurnReason" />
            </div>
            <span class="text-sm text-grey">Please select a reason for setting this clinic to {{ reasonSidebarStatusLabel }}.</span>
            <vs-select
              v-model="clinicData.churnReason"
              name="churnReason"
              data-vv-scope="basic"
              :danger="errors.first('basic.churnReason') ? true : false"
              :danger-text="errors.first('basic.churnReason')"
              v-validate="showReasonRequiredForStatus ? 'required' : ''"
              label="Churn Reason"
              autocomplete
              class="w-full"
            >
              <vs-select-item
                v-for="(item, index) in churnReasons"
                :key="index"
                :value="item.name"
                :text="item.name"
              />
            </vs-select>
            <div class="w-full mt-4">
              <label class="vs-input--label">Inactive Date</label>
              <datepicker
                format="dd/MM/yyyy"
                v-model="clinicData.churnDate"
                :input-class="{
                  'is-true': clinicData.churnDate,
                  'is-danger': false,
                }"
              >
                <template slot="afterDateInput">
                  <span
                    v-if="clinicData.churnDate"
                    class="
                      bg-color-done
                      input-icon-validate
                      vs-input--icon-validate
                    "
                  >
                    <i
                      valiconpack="material-icons"
                      class="vs-icon notranslate icon-scale material-icons null"
                      >done</i
                    >
                  </span>
                </template>
              </datepicker>
            </div>
            <div class="flex gap-2 mt-2 justify-between items-center">
              <vs-button type="line" class="p-0 pb-1 text-xs text-left" @click="openChurnManager">Manage Reasons</vs-button>
              <div>
                <vs-button @click="confirmChurnReason" type="filled">Confirm</vs-button>
              </div>
            </div>
          </div>
        </vs-sidebar>

        <!-- Manage Churn Reasons Popup -->
        <ChurnReasonManager v-model="churnManagerActive" ref="churnManager" />

        <div class="w-full mt-6">
          <vs-input
            :success-text="sageSuccess"
            :success="sageSuccess ? true : false"
            :danger-text="sageError"
            :danger="sageError ? true : false"
            val-icon-success="done"
            val-icon-danger="error"
            name="sageClientId"
            data-vv-scope="basic"
            data-vv-as="Sage Client ID"
            label="Sage Client ID"
            class="w-full"
            readonly
            v-model="clinicData.sageClientId"
          />
        </div>

        <div class="w-full mt-6 flex items-center space-x-3 justify-start lg:justify-start">
          <label for="admin_approval" class="font-medium">Admin Approval</label>
          <vs-switch
            id="admin_approval"
            class="my-1"
            @click="changeAdminApproval(clinicData.hasApprovedByAdmin)"
            v-model="adminToggle"
          />
        </div>
      </div>

      <div id="resetPasswordContainer">
        <vs-card v-if="resetPassword == true">
          <h4 class="mb-2 ml-5 mt-2">Reset Password</h4>
          <form autocomplete="off">
            <vs-row
              vs-align="flex-start"
              vs-type="flex"
              vs-justify="left"
              vs-w="12"
            >
              <vs-col vs-type="flex" vs-justify="left" vs-align="left" vs-w="6">
                <div class="w-full m-5">
                  <vs-input
                    :success="
                      !errors.first('resetPassword.newPassword') && password != ''
                    "
                    val-icon-success="done"
                    val-icon-danger="error"
                    :danger="
                      errors.first('resetPassword.newPassword') ? true : false
                    "
                    name="newPassword"
                    :danger-text="errors.first('resetPassword.newPassword')"
                    label="New Password"
                    class="w-full"
                    v-validate="{
                      required: true,
                      strongPassword: true
                    }"
                    data-vv-as="new password"
                    data-vv-scope="resetPassword"
                    ref="password"
                    icon-no-border
                    icon-pack="feather"
                    v-model="password"
                    :icon="passwordIcon"
                    :type="passwordType"
                    @click.native="showHidePassword($event)"
                  />
                </div>
              </vs-col>
              <vs-col vs-type="flex" vs-justify="left" vs-align="left" vs-w="6">
                <div class="w-full m-5">
                  <vs-input
                    label="Confirm Password"
                    :success="
                      !errors.first('resetPassword.confirmPassword') &&
                      confirmPassword != ''
                    "
                    val-icon-success="done"
                    val-icon-danger="error"
                    :danger="
                      errors.first('resetPassword.confirmPassword') ? true : false
                    "
                    name="confirmPassword"
                    :danger-text="errors.first('resetPassword.confirmPassword')"
                    v-validate="'required|confirmed:password'"
                    data-vv-as="confirm password"
                    data-vv-scope="resetPassword"
                    class="w-full"
                    icon-no-border
                    icon-pack="feather"
                    v-model="confirmPassword"
                    :icon="confirmPasswordIcon"
                    :type="confirmPasswordType"
                    @click.native="showHideConfirmPassword($event)"
                  />
                </div>
              </vs-col>
              <vs-col vs-type="flex" vs-align="left" vs-w="6">
                <div class="w-full m-5">
                  <vs-button
                    size="lg"
                    @click="changePassword"
                    icon-pack="feather"
                    class="mr-2"
                    >Reset Password</vs-button
                  >
                </div>
              </vs-col>
              <vs-col vs-type="flex" vs-align="left" vs-w="6">
                <div class="w-full m-5" align="right">
                  <vs-button
                    @click="resetPassword = false"
                    color="danger"
                    icon-pack="feather"
                    class="mr-2"
                    >Cancel</vs-button
                  >
                </div>
              </vs-col>
            </vs-row>
          </form>
        </vs-card>
      </div>
    </vs-card>
    <LogsSidebar :activity_logs="activityLogs" ref="logsSidebar" />
    <oversight-products
      :clinicId="clinicId"
      :selectedProducts="selectedProducts"
      @handleSave="(selected) => onSelectProducts(selected)"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { Validator } from 'vee-validate';
import axios from '@/axios';
import states from 'states-us';
import { normalizeStateName, getAUStatesForSelect } from '../../../utils/stateNormalizer';
import Datepicker from 'vuejs-datepicker';

import { EventBus } from '../../../main';
import mapboxgl from 'mapbox-gl';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import changeOrg from '@/views/components/others/changeOrganisation.vue';
import { updatedFieldChecker } from '../../../utils/updatedFieldChecker';
import LogsSidebar from '@/views/components/account-creation-logs/logs.vue';
import OversightProducts from './ClinicOversightProducts.vue';
import { isEqual, cloneDeep } from 'lodash';
import { loadGoogleMapsPlaces } from '../../../utils/loadGoogleMapPlacesAPI';
import ChurnReasonManager from './ChurnReasonManager.vue';

const CLINIC_STATUS_ACTIVE = 'Active';
const CLINIC_STATUS_DISABLED = 'Disabled';
const CLINIC_STATUS_CONSUMABLES = 'Consumables';
const CLINIC_STATUS_ON_HOLD = 'On Hold';

const CLINIC_STATUS_OPTIONS = Object.freeze([
  { value: CLINIC_STATUS_ACTIVE, text: 'Active' },
  // Label shown to users is "Inactive", stored backend value is "Disabled"
  { value: CLINIC_STATUS_DISABLED, text: 'Inactive' },
  // Label shown to users is "Consumable Only", stored backend value is "Consumables"
  { value: CLINIC_STATUS_CONSUMABLES, text: 'Consumable Only' },
  { value: CLINIC_STATUS_ON_HOLD, text: 'On Hold' },
]);

const dict = {
  custom: {
    bestContactNumber: {
      required: 'Please enter your best contact number',
    },
    newPassword: {
      required: 'Please enter your password',
      min: 'Password must be at least 10 characters',
      regex: 'Must contain at least one uppercase letter, one lowercase letter, one digit and one special character',
    },
    confirmPassword: {
      required: 'Please enter your confirm password',
      confirmed: 'The password confirmation does not match',
    },
  },
};

Validator.localize('en', dict);

export default {
  name: 'clinicEditDetail',
  inject: ['$validator'],
  props: {
    backRoute: {
      type: String,
      default: 'SuperAdminClinic',
    },
    organizationDetailRoute: {
      type: String,
      default: 'SuperAdminOrganizationDetails',
    },
  },

  components: {
    'change-org': changeOrg,
    LogsSidebar,
    OversightProducts,
    ChurnReasonManager,
    Datepicker,
  },
  data: () => ({
    changeOrgPopup: false,
    organizationDetail: {},
    isRequired: true,
    discounts: [],
    suppliers: [],
    validationKeys: {
      showIsResidentialAddressError: false,
      showIntendToSecureS4MedicationError: false,
      showHaveAdrenalineAndHyalaseInClinicError: false,
    },
    statusChangedClick: false,
    churnSidebarActive: false,
    churnManagerActive: false,
    churnReasons: [],
    adminApprovalChangeClick: false,
    discountEnableChangeClick: false,
    clinicId: '',
    password: '',
    confirmPassword: '',
    popUpDelete: false,
    clinicData: {
      isProfileCompleted: false,
      clinicAssignedSuppliers: [],
      clinicDiscounts: [],
      firstName: '',
      lastName: '',
      companyName: '',
      clinicName: '',
      abn: '',
      address: {
        displayAddress: '',
        state: '',
        suburb: '',
        postCode: '',
        geometry: { type: 'Point', coordinates: [] },
      },
      postalAddress: '',
      bestContactName: '',
      bestContactNumber: '',
      bestContactEmail: '',
      billingEmail: '',
      invoiceEmail: '',
      churnReason: '',
      churnDate: null,
      clinicProductDiscountId: null,
      isAccessPaymentTerms: false,
      isEnableProductDiscount: false,
      status: '',
      hasApprovedByAdmin: false,
      emailVerifiedAt: null,
      contactNumber: null,
      instagram: '',
      facebook: '',
      website: '',
      clinicAddress1: null,
      isResidentialAddress: null,
      haveAdrenalineAndHyalaseInClinic: null,
      intendToSecureS4Medication: null,
      abnCertificate: null,
      councilPermits: null,
      poisonsCertificate: null,
      clinicOpeningHours: {
        mondays: '',
        tuesdays: '',
        wednesdays: '',
        thursdays: '',
        fridays: '',
        saturdays: '',
        sundays: '',
      },
      clinicFrontPhoto: null,
      clinicFloorPhoto: null,
      clinicTreatmentBedPhoto: null,
      clinicDrawingUpAreaPhoto: null,
      clinicFridgePhoto: null,
      clinicDrugStoragePhoto: null,
      clinicBinsPhoto: null,
      clinicRoomLockPhoto: null,
      clinicEpipenAndHyalasePhoto: null,
      removedImages: null,
      sageClientId: '',
      isTrainingClinic: null,
      isWeightLossTreatment: false,
      isHRTTreatment: false,
      isStaffClinic: null,
      isS4Medication: false,
      isPOSEnabled: null,
      isInventoryEnabled: null,
      isCreditCardFeeExempt: null,
      isTaxable: null,
      isTestClinic: null,
      showStore: null,
      doctorApprovalFlowEnabled: false,
      complianceModel: null,
    },
    resetPassword: false,
    checkDisplayAddress: '',
    addressData: [],
    passwordType: 'password',
    passwordIcon: 'icon icon-eye-off',
    confirmPasswordType: 'password',
    confirmPasswordIcon: 'icon icon-eye-off',
    region: process.env.VUE_APP_REGION,
    accessToken: process.env.VUE_APP_MAPBOX_API_KEY,
    sageError:'',
    sageSuccess:'',
    updater: null,
    oldData: null,
    oldAddress: null,
    active: false,
    activityLogs: null,
    adminToggle: false,
    selectedStatus: null,
    selectedProducts: [],
    tierPackages: [],
    isFetching: true,
    preDefInactiveReasons: [],
    inactiveReasonPopupActivo: false,
    currentActiveReason: {
      reason: '',
      id: '',
    },
    isEditInactiveReason: false,
    currentlyDeletingInactiveReasonId: '',
    stateOptions: [],
    doctors: [],
    selected: null,
    models: [
      {value: 'Model 1', text: 'Model 1'},
      {value: 'Model 2', text: 'Model 2'},
      {value: 'Model 3', text: 'Model 3'},
      {value: null, text: 'Unassigned'},
    ],
    selectedModel: null,
  }),
  computed: {
    clinicStatusOptions() {
      return CLINIC_STATUS_OPTIONS;
    },
    /** Shown when status is Inactive or On Hold — reason is required and the reason drawer is available. */
    showReasonRequiredForStatus() {
      return this.selectedStatus === CLINIC_STATUS_DISABLED || this.selectedStatus === CLINIC_STATUS_ON_HOLD;
    },
    /** Label for the reason sidebar title/description (use status we're setting; clinicData.status is set before sidebar opens). */
    reasonSidebarStatusLabel() {
      const status = this.clinicData.status || this.selectedStatus || '';
      return (status === CLINIC_STATUS_ON_HOLD || String(status).toLowerCase() === 'on hold')
        ? 'On Hold'
        : 'Inactive';
    },
  },
  methods: {
    ...mapActions('clinic', [
      'fetchClinicsDetail',
      'updateClinicDetail',
      'resetClinicPassword',
      'deleteClinic',
      'removeClinicImages',
    ]),
    ...mapActions('discount', ['fetchDiscountList']),
    ...mapActions('storeProducts', ['fetchSuppliers']),
    ...mapActions('appUser', ['getAccountCreationLogs']),
    ...mapActions('clinicInactiveReasons', [
      'fetchClinicInactiveReasons',
      'createClinicInactiveReason',
      'updateClinicInactiveReason',
      'deleteClinicInactiveReason',
    ]),
    ...mapActions('admin', [
      'getSettingsByKey',
      'fetchOversightDoctors',
      'assignOversightDoctors',
      'listAssignedOversightDoctors',
    ]),
    ...mapActions('churnReasons', [
      'fetchReasons',
    ]),
    async getChurnReasons() {
      try {
        const { data } = await this.fetchReasons();
        this.churnReasons = (data && data.data) ? data.data : [];
      } catch (e) {
        this.churnReasons = [];
      }
    },
    async getAssignedOversightDoctors() {
      try {
        const { data } = await this.listAssignedOversightDoctors({
          clinicId: this.clinicId,
        });
        const assignedDoctors = (data.data || []).map((doctor) => ({
          _id: doctor._id,
          name: `${doctor.firstName} ${doctor.lastName}`,
        }));

        this.oldData.oversightDoctorsName = assignedDoctors.length ? assignedDoctors[0].name : null;
        this.oldData.oversightDoctors = assignedDoctors.length ? assignedDoctors[0]._id : null;
        this.selected = assignedDoctors.length ? assignedDoctors[0]._id : null;
      } catch (err) {
        console.error('Error fetching assigned oversight doctors::: ', err);
      }
    },
    async getOversightDoctors() {
      this.doctors = [];

      try {
        const { data } = await this.fetchOversightDoctors({
          search: this.searchString, clinicId: this.clinicId,
        });

        this.doctors = (data.data || []).map((doctor) => ({
          _id: doctor._id,
          name: `${doctor.firstName} ${doctor.lastName}`,
        }));
      } catch (err) {
        console.error('Error fetching oversight doctors::: ', err);
      }
    },
    resetPasswordHandler() {
      this.resetPassword = true;
      window.scrollTo(0, document.body.scrollHeight);
    },
    async changePassword() {
      const isValid = await this.$validator.validateAll('resetPassword');
      if (isValid) {
        await this.resetClinicPassword({
          id: this.clinicId,
          data: {
            password: this.password,
            confirmPassword: this.confirmPassword,
          },
        }).then(() => {
          this.password = '';
          this.confirmPassword = '';
          this.$validator.reset();
          this.$vs.notify({
            title: 'Clinic password reset',
            text: 'Clinic\'s password reset Successfully.',
            color: 'success',
          });
        });
      }
    },
    openOrganization() {
      this.$router.push({
        name: this.organizationDetailRoute,
        params: { organizationId: this.organizationDetail._id },
      });
    },
    closeOrganization() {
      this.changeOrgPopup = false;
    },
    determineFileType(str) {
      var res = str.substring(str.length - 3, str.length);
      return res;
    },
    uploadImage(event, key = 'profileImage', loadingClass = '') {
      this.$vs.loading({
        background: '#fff',
        color: '#626262',
        container: `.${loadingClass}`,
        scale: 0.45,
      });
      let data = new FormData();
      data.append('profileImage', event.target.files[0]);
      let config = {
        header: {
          'Content-Type': 'image/png',
        },
      };
      axios
        .post('/api/v1/doctors/upload?key=' + key, data, config)
        .then((res) => {
          if (key === 'abnCertificateUpload') {
            document.getElementById('abnCertificateId').style.borderColor =
              '#cccccc';
            this.clinicData.abnCertificate = res.data.Location;
            this.errors.remove('abnCertificate');
          }

          this.$vs.loading.close(`.${loadingClass} > .con-vs-loading`);
        })
        .catch((err) => {
          this.$vs.loading.close(`.${loadingClass} > .con-vs-loading`);
          if (err && err.status === 415) {
            this.$vs.notify({
              title: 'Upload Failed',
              text: 'File Format Not Supported',
              iconPack: 'feather',
              icon: 'icon-alert-circle',
              color: 'danger',
            });
          } else {
            this.$vs.notify({
              title: 'Upload Failed',
              text: 'Please try again later.',
              iconPack: 'feather',
              icon: 'icon-alert-circle',
              color: 'danger',
            });
          }
        });
    },
    removeImage(imageUrl, selector, loadingClass) {
      this.clinicData[loadingClass] = this.clinicData[loadingClass].filter(
        (value) => value.photoUrl != imageUrl
      );

      this.clinicData.removedImages.push(imageUrl);
    },
    showHidePassword(event) {
      if (
        event.target.className ===
          'vs-icon notranslate icon-scale icon-inputx notranslate vs-input--icon feather icon icon-eye-off null icon-no-border' ||
        event.target.className ===
          'vs-icon notranslate icon-scale icon-inputx notranslate vs-input--icon feather icon icon-eye null icon-no-border'
      ) {
        this.passwordType === 'password'
          ? (this.passwordType = 'text')
          : (this.passwordType = 'password');
        this.passwordIcon === 'icon icon-eye-off'
          ? (this.passwordIcon = 'icon icon-eye')
          : (this.passwordIcon = 'icon icon-eye-off');
      }
      return true;
    },
    showHideConfirmPassword(event) {
      if (
        event.target.className ===
          'vs-icon notranslate icon-scale icon-inputx notranslate vs-input--icon feather icon icon-eye-off null icon-no-border' ||
        event.target.className ===
          'vs-icon notranslate icon-scale icon-inputx notranslate vs-input--icon feather icon icon-eye null icon-no-border'
      ) {
        this.confirmPasswordType === 'password'
          ? (this.confirmPasswordType = 'text')
          : (this.confirmPasswordType = 'password');
        this.confirmPasswordIcon === 'icon icon-eye-off'
          ? (this.confirmPasswordIcon = 'icon icon-eye')
          : (this.confirmPasswordIcon = 'icon icon-eye-off');
      }
      return true;
    },
    async getClinicDetail(id) {
      await this.fetchClinicsDetail(id)
        .then((res) => {
          const clinic = res.data.data;
          
          if (clinic.products && clinic.products.length) {
            this.selectedProducts = clinic.products.map(
              ({ productId }) => productId
            );
          }
          this.selectedModel = clinic.complianceModel ? clinic.complianceModel : null;
          this.organizationDetail = { ...clinic.organizationDetail };
          this.clinicId = clinic._id;
          this.clinicData.firstName = clinic.firstName;
          this.clinicData.lastName = clinic.lastName;
          this.clinicData.companyName = clinic.companyName;
          this.clinicData.clinicName = clinic.clinicName;
          this.clinicData.abn = clinic.abn;
          this.clinicData.streetAddress = clinic.address
            ? clinic.address.displayAddress
            : '';
          this.clinicData.postalAddress = clinic.postalAddress;
          this.clinicData.bestContactName = clinic.bestContactName;
          this.clinicData.bestContactNumber = clinic.bestContactNumber;
          this.clinicData.bestContactEmail = clinic.bestContactEmail;
          this.clinicData.billingEmail = clinic.billingEmail;
          this.clinicData.invoiceEmail = clinic.invoiceEmail;
          this.clinicData.isAccessPaymentTerms =
            typeof clinic.isAccessPaymentTerms !== 'undefined'
              ? clinic.isAccessPaymentTerms
              : false;
          this.clinicData.isBYODoctor =
            typeof clinic.isBYODoctor !== 'undefined'
              ? clinic.isBYODoctor
              : false;
          this.clinicData.isS4Medication = clinic.isS4Medication
            ? clinic.isS4Medication
            : false;
          this.clinicData.doctorApprovalFlowEnabled = clinic.doctorApprovalFlowEnabled
            ? clinic.doctorApprovalFlowEnabled
            : false;
          this.clinicData.isEnableProductDiscount =
            typeof clinic.isEnableProductDiscount !== 'undefined'
              ? clinic.isEnableProductDiscount
              : false;
          this.clinicData.clinicProductDiscountId =
            clinic.clinicProductDiscountId
              ? clinic.clinicProductDiscountId
              : null;
          this.clinicData.clinicAssignedSuppliers =
            clinic.clinicAssignedSuppliers
              ? clinic.clinicAssignedSuppliers
              : [];
          this.clinicData.clinicDiscounts =
            clinic.clinicDiscounts
              ? clinic.clinicDiscounts
              : [];
          this.clinicData.address = clinic.address;
          // Normalize and validate the state value if it exists
          if (this.clinicData.address && this.clinicData.address.state) {
            this.clinicData.address.state = this.validateAndNormalizeState(this.clinicData.address.state);
          }
          this.checkDisplayAddress = clinic.address ? clinic.address.displayAddress : null;
          this.clinicData.hasApprovedByAdmin = clinic.hasApprovedByAdmin ? clinic.hasApprovedByAdmin : false;
          this.clinicData.emailVerifiedAt = clinic.emailVerifiedAt;
          this.clinicData.email = clinic.email;
          this.clinicData.bestContactNumber = clinic.bestContactNumber
            ? clinic.bestContactNumber
            : '';
          this.clinicData.instagram = clinic.instagram ? clinic.instagram : '';
          this.clinicData.facebook = clinic.facebook ? clinic.facebook : '';
          this.clinicData.website = clinic.website ? clinic.website : '';
          this.clinicData.clinicAddress1 = clinic.clinicAddress1
            ? clinic.clinicAddress1
            : null;
          this.clinicData.isResidentialAddress = clinic.isResidentialAddress
            ? clinic.isResidentialAddress
            : null;
          this.clinicData.haveAdrenalineAndHyalaseInClinic =
            clinic.haveAdrenalineAndHyalaseInClinic
              ? clinic.haveAdrenalineAndHyalaseInClinic
              : null;
          this.clinicData.intendToSecureS4Medication =
            clinic.intendToSecureS4Medication
              ? clinic.intendToSecureS4Medication
              : null;
          this.clinicData.abnCertificate = clinic.abnCertificate
            ? clinic.abnCertificate
            : null;
          this.clinicData.councilPermits = clinic.councilPermits
            ? clinic.councilPermits
            : null;
          this.clinicData.poisonsCertificate = clinic.poisonsCertificate
            ? clinic.poisonsCertificate
            : null;
          this.clinicData.clinicOpeningHours = clinic.clinicOpeningHours
            ? clinic.clinicOpeningHours
            : {
                mondays: '',
                tuesdays: '',
                wednesdays: '',
                thursdays: '',
                fridays: '',
                saturdays: '',
                sundays: '',
              };
          this.clinicData.clinicFrontPhoto = clinic.clinicFrontPhoto
            ? clinic.clinicFrontPhoto
            : null;
          this.clinicData.clinicFloorPhoto = clinic.clinicFloorPhoto
            ? clinic.clinicFloorPhoto
            : null;
          this.clinicData.clinicTreatmentBedPhoto =
            clinic.clinicTreatmentBedPhoto
              ? clinic.clinicTreatmentBedPhoto
              : null;
          this.clinicData.clinicDrawingUpAreaPhoto =
            clinic.clinicDrawingUpAreaPhoto
              ? clinic.clinicDrawingUpAreaPhoto
              : null;
          this.clinicData.clinicFridgePhoto = clinic.clinicFridgePhoto
            ? clinic.clinicFridgePhoto
            : null;
          this.clinicData.clinicDrugStoragePhoto = clinic.clinicDrugStoragePhoto
            ? clinic.clinicDrugStoragePhoto
            : null;
          this.clinicData.clinicBinsPhoto = clinic.clinicBinsPhoto
            ? clinic.clinicBinsPhoto
            : null;
          this.clinicData.clinicRoomLockPhoto = clinic.clinicRoomLockPhoto
            ? clinic.clinicRoomLockPhoto
            : null;
          this.clinicData.clinicEpipenAndHyalasePhoto =
            clinic.clinicEpipenAndHyalasePhoto
              ? clinic.clinicEpipenAndHyalasePhoto
              : null;
          this.clinicData.isProfileCompleted =
            clinic.isProfileCompleted || false;
          this.clinicData.isTrainingClinic = clinic.isTrainingClinic
            ? clinic.isTrainingClinic : null;
          this.clinicData.isStaffClinic = clinic.isStaffClinic
            ? clinic.isStaffClinic : null;
          this.clinicData.isDrugBookEnabled = clinic.isDrugBookEnabled
            ? clinic.isDrugBookEnabled
            : null;
          this.clinicData.isPOSEnabled = clinic.isPOSEnabled
            ? clinic.isPOSEnabled
            : null;
          this.clinicData.isInventoryEnabled = clinic.isInventoryEnabled
            ? clinic.isInventoryEnabled
            : null;
          this.clinicData.isCreditCardFeeExempt = clinic.isCreditCardFeeExempt
            ? clinic.isCreditCardFeeExempt
            : null;
          this.clinicData.isTaxable = clinic.isTaxable
            ? clinic.isTaxable
            : null;
          this.clinicData.isTestClinic = clinic.isTestClinic
            ? clinic.isTestClinic
            : null;
          this.clinicData.isWeightLossTreatment = clinic.isWeightLossTreatment ? clinic.isWeightLossTreatment : null;
          this.clinicData.isHRTTreatment = clinic.isHRTTreatment ? clinic.isHRTTreatment : null;
          this.clinicData.showStore = clinic.showStore ? clinic.showStore : null;
          this.clinicData.paymentTypes = clinic.paymentTypes
            ? clinic.paymentTypes
            : null;
          this.clinicData.orderRecipients = clinic.orderRecipients
            ? clinic.orderRecipients.join(',')
            : [];
          this.clinicData.sageClientId = clinic.sageClientId;
          // Backend stores explicit status values (e.g. 'Active', 'Disabled', 'Consumables')
          this.selectedStatus = clinic.status || 'Active';
          this.clinicData.churnReason = clinic.churn && clinic.churn.name ? clinic.churn.name : '';
          this.clinicData.churnDate = clinic.churn && clinic.churn.churnDate ? new Date(clinic.churn.churnDate) : null;
          this.clinicData.oversightProducts = clinic.products
            ? clinic.products.map(({ productName }) => productName).join(', ')
            : '';

          this.clinicData.storeInvoiceEmail = clinic.storeInvoiceEmail;
          this.clinicData.isDoctorStore = clinic.isDoctorStore;

          this.oldAddress = cloneDeep(clinic.address);
          this.oldData = cloneDeep(this.clinicData);

          this.oldData.hasApprovedByAdmin = clinic.hasApprovedByAdmin ? clinic.hasApprovedByAdmin : false;
          this.oldData.orderRecipients = clinic.orderRecipients ? clinic.orderRecipients.join(',') : [];
          this.oldData.status = clinic.status;
          this.oldData.oversightProducts = clinic.products ? clinic.products.map(({ productName }) => productName).join(', ') : '';

          this.adminToggle = clinic.hasApprovedByAdmin ? clinic.hasApprovedByAdmin : false;

          return this.fetchSuppliers();
        })
        .then((suppliers) => {
          this.suppliers = suppliers.data.data;
          this.getSageCustomerInfo();
        });
    },
    async getDiscountList() {
      await this.fetchDiscountList().then((response) => {
        this.discounts = response.data.data;
      });
    },
    onSuccessfullyVerified(verifiedAt) {
      this.clinicData.emailVerifiedAt = verifiedAt;
    },
    async saveData() {
      // If clinic is set to Disabled (Inactive), churn reason is mandatory
      const isInactive = this.selectedStatus === CLINIC_STATUS_DISABLED;
      const hasNoChurnReason = !this.clinicData.churnReason || !`${this.clinicData.churnReason}`.trim();
      
      if (isInactive && hasNoChurnReason) {
        this.errors.add({ field: 'churnReason', msg: 'Churn reason is required', scope: 'basic' });
        await this.getChurnReasons();
        this.churnSidebarActive = true;
        return;
      }

      let isValid = await this.$validator.validateAll('basic');

      if (
        this.$store.state.AppActiveUser.userType !== 'superAdmin' &&
        this.$store.state.AppActiveUser.userType !== 'admin' &&
        this.$store.state.AppActiveUser.userType !== 'franchise' &&
        this.$store.state.AppActiveUser.userType !== 'subFranchise'
      ) {
        isValid = await this.$validator.validate();
        isValid = await this.$refs.stepTwoRef.$validator.validate();
        isValid = await this.$refs.stepThreeRef.$validator.validate();
        isValid = await this.$refs.step4Ref.$validator.validate();
        isValid = await this.$refs.step5Ref.$validator.validate();
        isValid = await this.$refs.step6Ref.$validator.validate();
        isValid = await this.$refs.step7Ref.$validator.validate();
        isValid = await this.$refs.step8Ref.$validator.validate();
        isValid = await this.$refs.step9Ref.$validator.validate();
        isValid = await this.$refs.step10Ref.$validator.validate();
        isValid = await this.$refs.step11Ref.$validator.validate();

        if (this.clinicData.isResidentialAddress === '') {
          isValid = false;
          this.validationKeys.showIsResidentialAddressError = true;
        } else {
          this.validationKeys.showIsResidentialAddressError = false;
        }

        if (this.clinicData.intendToSecureS4Medication === '') {
          isValid = false;
          this.validationKeys.showIntendToSecureS4MedicationError = true;
        } else {
          this.validationKeys.showIntendToSecureS4MedicationError = false;
        }

        if (this.clinicData.haveAdrenalineAndHyalaseInClinic === '') {
          isValid = false;
          this.validationKeys.showHaveAdrenalineAndHyalaseInClinicError = true;
        } else {
          this.validationKeys.showHaveAdrenalineAndHyalaseInClinicError = false;
        }

        if (this.clinicData.abnCertificate == '') {
          this.errors.add({
            field: 'abnCertificate',
            msg: 'ABN Certificate is required',
          });
          document.getElementById('abnCertificateId').style.borderColor =
            '#ea5455';
          isValid = false;
        } else {
          document.getElementById('abnCertificateId').style.borderColor =
            '#cccccc';
          this.errors.remove('abnCertificate');
        }

        if (this.clinicData.clinicFrontPhoto.length === 0) {
          this.$refs.stepThreeRef.errors.add({
            field: 'clinicFrontPhoto',
            msg: 'Clinic Front Photo is required',
          });
          document.getElementById('clinicFrontPhoto').style.borderColor =
            '#ea5455';
          isValid = false;
        } else {
          document.getElementById('clinicFrontPhoto').style.borderColor =
            '#cccccc';
          this.$refs.stepThreeRef.errors.remove('clinicFrontPhoto');
        }

        if (this.clinicData.clinicFloorPhoto.length === 0) {
          this.$refs.step4Ref.errors.add({
            field: 'clinicFloorPhoto',
            msg: 'Clinic Floor Photo is required',
          });
          document.getElementById('clinicFloorPhoto').style.borderColor =
            '#ea5455';
          isValid = false;
        } else {
          document.getElementById('clinicFloorPhoto').style.borderColor =
            '#cccccc';
          this.$refs.step4Ref.errors.remove('clinicFloorPhoto');
        }

        if (this.clinicData.clinicTreatmentBedPhoto.length === 0) {
          this.$refs.step5Ref.errors.add({
            field: 'clinicTreatmentBedPhoto',
            msg: 'Clinic Treatment Bed Photo is required',
          });
          document.getElementById('clinicTreatmentBedPhoto').style.borderColor =
            '#ea5455';
          isValid = false;
        } else {
          document.getElementById('clinicTreatmentBedPhoto').style.borderColor =
            '#cccccc';
          this.$refs.step5Ref.errors.remove('clinicTreatmentBedPhoto');
        }

        if (this.clinicData.clinicDrawingUpAreaPhoto.length === 0) {
          this.$refs.step6Ref.errors.add({
            field: 'clinicDrawingUpAreaPhoto',
            msg: 'Clinic Drawing Up Area Photo is required',
          });
          document.getElementById(
            'clinicDrawingUpAreaPhoto'
          ).style.borderColor = '#ea5455';
          isValid = false;
        } else {
          document.getElementById(
            'clinicDrawingUpAreaPhoto'
          ).style.borderColor = '#cccccc';
          this.$refs.step6Ref.errors.remove('clinicDrawingUpAreaPhoto');
        }

        if (this.clinicData.clinicFridgePhoto.length === 0) {
          this.$refs.step7Ref.errors.add({
            field: 'clinicFridgePhoto',
            msg: 'Clinic Fridge Photo is required',
          });
          document.getElementById('clinicFridgePhoto').style.borderColor =
            '#ea5455';
          isValid = false;
        } else {
          document.getElementById('clinicFridgePhoto').style.borderColor =
            '#cccccc';
          this.$refs.step7Ref.errors.remove('clinicFridgePhoto');
        }

        if (this.clinicData.clinicDrugStoragePhoto.length === 0) {
          this.$refs.step8Ref.errors.add({
            field: 'clinicDrugStoragePhoto',
            msg: 'Clinic Drug Storage Photo is required',
          });
          document.getElementById('clinicDrugStoragePhoto').style.borderColor =
            '#ea5455';
          isValid = false;
        } else {
          document.getElementById('clinicDrugStoragePhoto').style.borderColor =
            '#cccccc';
          this.$refs.step8Ref.errors.remove('clinicDrugStoragePhoto');
        }

        if (this.clinicData.clinicBinsPhoto.length === 0) {
          this.$refs.step9Ref.errors.add({
            field: 'clinicBinsPhoto',
            msg: 'Clinic Bins Photo is required',
          });
          document.getElementById('clinicBinsPhoto').style.borderColor =
            '#ea5455';
          isValid = false;
        } else {
          document.getElementById('clinicBinsPhoto').style.borderColor =
            '#cccccc';
          this.$refs.step9Ref.errors.remove('clinicBinsPhoto');
        }
      }

      if (isValid) {
        this.$vs.loading();
        this.clinicData.clinicId = this.clinicId,
        this.clinicData.updater = `${this.updater.firstName} ${this.updater.lastName}`,
        this.clinicData.name = this.clinicData.clinicName,
        // ensure status on the payload matches the selected string status
        this.clinicData.status = this.selectedStatus,
        this.clinicData.updatedFields = updatedFieldChecker(this.oldData, {
          ...this.clinicData,
          status: this.selectedStatus,
        });

        if(this.oldData.hasApprovedByAdmin !== this.clinicData.hasApprovedByAdmin) {
          this.clinicData.updatedFields = {
            ...this.clinicData.updatedFields,
            hasApprovedByAdmin: {
              oldValue: this.oldData.hasApprovedByAdmin, newValue: this.clinicData.hasApprovedByAdmin,
            },
          };
        }

        if (this.oldData.status !== this.selectedStatus) {
          this.clinicData.updatedFields = {
            ...this.clinicData.updatedFields,
            status: {
              oldValue: this.oldData.status,
              newValue: this.selectedStatus,
            },
          };
        }

        this.clinicData.doctors = [this.selected];
        this.clinicData.complianceModel = this.selectedModel;
        if (!isEqual(this.oldData.oversightDoctors, this.selected)) {
          // Update the data object with the new and old oversight doctors names
          const newDoctorName = this.doctors.find(i => i._id === this.selected);
          this.clinicData.updatedFields = {
            ...this.clinicData.updatedFields,
            oversightDoctors: {
              oldValue: this.oldData.oversightDoctorsName,
              newValue: newDoctorName ? newDoctorName.name : '',
            },
          };
        }

        if (!isEqual(this.oldData.complianceModel, this.selectedModel)) {
          this.clinicData.updatedFields = {
            ...this.clinicData.updatedFields,
            complianceModel: {
              oldValue: this.oldData.complianceModel,
              newValue: this.selectedModel,
            },
          };
        }

        if (this.selectedModel === 'Model 3') {
          this.clinicData.isS4Medication = false;
        }
        if (!isEqual(this.oldData.isS4Medication, this.clinicData.isS4Medication)) {
          this.clinicData.updatedFields = {
            ...this.clinicData.updatedFields,
            isS4Medication: {
              oldValue: this.oldData.isS4Medication,
              newValue: this.clinicData.isS4Medication,
            },
          };
        }

        const selectedChurn = (this.churnReasons || []).find(item => item && item.name === this.clinicData.churnReason);
        const churnPayload = (this.clinicData.churnReason && selectedChurn && selectedChurn._id)
          ? { 
              id: selectedChurn._id, 
              name: this.clinicData.churnReason, 
              churnDate: this.clinicData.churnDate ? new Date(this.clinicData.churnDate).toISOString() : null, 
            }
          : null;

        // Remove churnReason and churnDate from clinicData spread to avoid conflict
        const clinicDataWithoutChurnReason = {...this.clinicData};
        delete clinicDataWithoutChurnReason.churnReason;
        delete clinicDataWithoutChurnReason.churnDate;

        await this.updateClinicDetail({
          id: this.clinicId,
          data: {
            ...clinicDataWithoutChurnReason,
            products: this.selectedProducts,
            churn: churnPayload,
          },
        }).then(() => {
            this.$vs.notify({
              title: 'Success',
              text: 'Clinic updated successfully.',
              color: 'success',
            });
            this.getClinicDetail(this.clinicId);
            this.$vs.loading.close();
          })
          .catch((err) => {
            this.$vs.notify({
              title: 'Failed',
              text: 'Failed to update clinic. Try again!',
              color: 'danger',
            });

            if (err.status === 422) {
              this.mapServerErrors(this, err, 'basic');
            }
            if (err.status === 417) {
              if (err.data.title === 'Invalid email') {
                this.errors.add({
                  field: 'email',
                  msg: err.data.message,
                  scope: 'basic',
                });
              } else if (err.data.title === 'Invalid ABN') {
                this.errors.add({
                  field: 'abn',
                  msg: err.data.message,
                  scope: 'basic',
                });
              }
            }
            this.$vs.loading.close();
          });
      }
    },
    async changeAdminApproval(status) {
      this.oldData.hasApprovedByAdmin = this.clinicData.hasApprovedByAdmin;
      this.clinicData.hasApprovedByAdmin = !status;
      this.adminToggle = status;
    },
    async onStatusChange(nextStatus) {
      // nextStatus is one of the defined clinic status constants
      this.selectedStatus = nextStatus;
      this.clinicData.status = nextStatus;

      if (nextStatus === CLINIC_STATUS_DISABLED || nextStatus === CLINIC_STATUS_ON_HOLD) {
        // Going to Inactive or On Hold: require reason and set default date, open sidebar
        if (!this.clinicData.churnDate) {
          this.clinicData.churnDate = new Date();
        }
        await this.getChurnReasons();
        this.churnSidebarActive = true;
      } else {
        // Going to Active or Consumables: clear reason and date
        this.clinicData.churnReason = null;
        this.clinicData.churnDate = null;
        this.errors.remove('churnReason');
      }
    },
    toggleChurnSidebar() {
      const next = !this.churnSidebarActive;
      if (next) {
        this.getChurnReasons();
      }
      this.churnSidebarActive = next;
    },
    confirmChurnReason() {
      // Ensure validation before closing
      this.$validator.validate('basic.churnReason').then((valid) => {
        if (valid) {
          this.churnSidebarActive = false;
        }
      });
    },
    cancelChurnReason() {
      // Close drawer only; do not alter status
      this.churnSidebarActive = false;
    },
    async changeDiscountEnable(status) {
      this.discountEnableChangeClick = true;
      await this.updateClinicDetail({
        id: this.clinicId,
        data: { isEnableProductDiscount: !status },
      })
        .then(() => {
          this.$vs.notify({
            title: 'Success',
            text: 'Discount enable updated successfully.',
            color: 'success',
          });
          this.discountEnableChangeClick = false;
        })
        .catch(() => {
          this.$vs.notify({
            title: 'Failed',
            text: 'Failed to update discount enable. Try again!',
            color: 'danger',
          });
          this.discountEnableChangeClick = false;
        });
    },
    mapServerErrors(self, err) {
      let data = err.data.data.details;
      data.map(function (value) {
        self.errors.add({
          field: value.context.key,
          msg: value.message.replace(/"/g, ''),
        });
      });
    },
    regionType() {
      const regions = {
        US: 'us',
        AU: 'au',
        NZ: 'nz',
      };
      return regions[this.region];
    },
    normalizeStateName(stateText) {
      const region = this.$isUSRegion() ? 'US' : (this.$isAuRegion() ? 'AU' : '');
      return normalizeStateName(stateText, region, this.stateOptions);
    },
    validateAndNormalizeState(stateValue) {
      if (!stateValue) return '';
      
      // Normalize the state value
      const normalized = this.normalizeStateName(stateValue);
      
      // Check if the normalized state exists in stateOptions
      if (this.stateOptions && this.stateOptions.length > 0) {
        const exists = this.stateOptions.some(
          (option) => option.name.toLowerCase() === normalized.toLowerCase()
        );
        return exists ? normalized : '';
      }
      
      return normalized;
    },
    async getSageCustomerInfo() {
        try {
            if (!this.clinicData.sageClientId) {
                this.sageSuccess = '';
                this.sageError = '';
                return;
            }

            this.$vs.loading();

            const {data} = await axios.get(`/sage/customer/${this.clinicData.sageClientId}`);

            if (data && data.data && data.data.NAME) {
                this.sageSuccess = `Customer ID ${this.clinicData.sageClientId} corresponds to an entity named ${data.data.NAME} with status ${data.data.STATUS} on the Sage platform.`;
                this.sageError = '';
            } else {
                this.sageSuccess = '';
                this.sageError = `The customer with ID ${this.clinicData.sageClientId} does not exist on the Sage platform.`;
            }
        } catch (e) {
            this.sageSuccess = '';
            this.sageError = `The customer with ID ${this.clinicData.sageClientId} does not exist on the Sage platform.`;
        } finally {
            this.$vs.loading.close();
        }
    },
    async validateSageCustomer() {
        if (this.timeout)
            clearTimeout(this.timeout);

        this.timeout = setTimeout(async () => {
            this.getSageCustomerInfo();
        }, 1000); // delay
    },
    async getAccountLogs() {
      this.$refs.logsSidebar.openSidebar();

      await this.getAccountCreationLogs({
        id: this.$route.params.clinicId, 
        type: 'clinic',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      })
        .then(res => {
         this.activityLogs = res;

        }).catch(err => {
          console.warn(err);
        });
    },
    onSelectProducts(selectedProducts) {
      this.selectedProducts = selectedProducts.map(({ _id }) => _id);
      this.clinicData.oversightProducts = selectedProducts && selectedProducts.length ? selectedProducts.map(({ name }) => name).join(', ') : '';
    },
    openChurnManager() {
      // Close drawer first, then open manager popup via v-model to avoid async ref timing
      this.churnSidebarActive = false;
      this.$nextTick(() => {
        this.churnManagerActive = true;
      });
    },
  },
  async mounted() {
    await loadGoogleMapsPlaces();

    this.autocomplete = new google.maps.places.Autocomplete(
      this.$refs.autocomplete,
      { types: ['geocode'], componentRestrictions: { country: this.regionType() } }
    );
    this.autocomplete.addListener('place_changed', () => {
      let place = this.autocomplete.getPlace();
      this.errors.remove('displayAddress');
      this.addressData = place.address_components;
      this.checkDisplayAddress = place.formatted_address;
      let lat = place.geometry.location.lat();
      let lon = place.geometry.location.lng();
      this.clinicData.address.displayAddress = place.formatted_address;
      this.clinicData.address.geometry.coordinates[0] = lon;
      this.clinicData.address.geometry.coordinates[1] = lat;

      this.clinicData.address.suburb = '';
      this.clinicData.address.state = '';
      this.clinicData.address.postCode = '';
      if (
        place &&
        place.address_components &&
        place.address_components.length > 0
      ) {
        place.address_components.forEach(function (gAdd) {
          if (gAdd.types[0] === 'administrative_area_level_2') {
            this.clinicData.address.suburb = gAdd.long_name;
            this.errors.remove('clinicSuburb');
          }
          if (gAdd.types[0] === 'administrative_area_level_1') {
            const stateText = gAdd.long_name || gAdd.short_name || '';
            this.clinicData.address.state = this.normalizeStateName(stateText);
            this.errors.remove('clinicState');
          }
          if (gAdd.types[0] === 'postal_code') {
            this.clinicData.address.postCode = gAdd.long_name;
            this.errors.remove('clinicPostCode');
          }
        });
      }
    });
    // Populate state options based on region
    if(this.$isUSRegion()) {
      if(states && states.length > 0) {
        this.stateOptions = states;
      }
    } else if(this.$isAuRegion()) {
      this.stateOptions = getAUStatesForSelect();
    }
    
    // Normalize existing state value after stateOptions are populated
    if (this.clinicData.address && this.clinicData.address.state) {
      this.clinicData.address.state = this.validateAndNormalizeState(this.clinicData.address.state);
    }
  },
  async created() {
    this.updater = JSON.parse(
      localStorage.getItem(`${process.env.VUE_APP_ACCESS_TOKEN_NAME}User`)
    );

    let clinicId = this.$route.params.clinicId;
    await this.getClinicDetail(clinicId);
    await this.getAssignedOversightDoctors();
    await this.getOversightDoctors();
    this.isRequired =
      this.$store.state.AppActiveUser.userType === 'superAdmin' ||
      this.$store.state.AppActiveUser.userType === 'admin' ||
      this.$store.state.AppActiveUser.userType === 'franchise' ||
      this.$store.state.AppActiveUser.userType === 'subFranchise'
        ? false
        : true;
    await this.getDiscountList();
    mapboxgl.accessToken = this.accessToken;

    var geocoder = new MapboxGeocoder({
      accessToken: mapboxgl.accessToken,
      types: 'address',
      countries: this.regionType(),
      placeholder: this.clinicData.address ? this.clinicData.address.displayAddress : null,
    });

    geocoder.addTo('#geocoder');

    geocoder.on('results', (e) => {
      this.clinicData.address.displayAddress = e.config.query;
      this.clinicData.address.suburb = '';
      this.clinicData.address.state = '';
      this.clinicData.address.postCode = '';
      this.clinicData.address.coordinates = {
        longitude: '',
        latitude: '',
      };
    });

    geocoder.on('result', (e) => {
      const regionType = this.region === 'US' ? 'district' : 'locality';

      const suburbItem = e.result.context.find(function(item) {
        return item.id.includes(regionType);
      });
      this.clinicData.address.suburb = suburbItem ? suburbItem.text : '';

      const stateItem = e.result.context.find(function(item) {
        return item.id.includes('region');
      });

      this.clinicData.address.state = this.normalizeStateName(stateItem ? stateItem.text : '');

      const postcodeItem = e.result.context.find(function(item) {
        return item.id.includes('postcode');
      });
      this.clinicData.address.postCode = postcodeItem ? postcodeItem.text : '';

      this.clinicData.address.displayAddress = e.result.place_name;

      this.clinicData.address.coordinates = {
        longitude: e.result.center[0],
        latitude: e.result.center[1],
      };
    });
  },
  watch: {
    'clinicData.isResidentialAddress': function (newVal) {
      if (newVal !== '') {
        this.validationKeys.showIsResidentialAddressError = false;
      }
    },
    'clinicData.intendToSecureS4Medication': function (newVal) {
      if (newVal !== '') {
        this.validationKeys.showIntendToSecureS4MedicationError = false;
      }
    },
    'clinicData.haveAdrenalineAndHyalaseInClinic': function (newVal) {
      if (newVal !== '') {
        this.validationKeys.showHaveAdrenalineAndHyalaseInClinicError = false;
      }
    },
    'selectedModel': function (newVal) {
        if (newVal === null || newVal === 'Model 3'){
          this.clinicData.doctorApprovalFlowEnabled = false;
        }
        else{
          this.clinicData.doctorApprovalFlowEnabled = true;
        }
    },
  },
  beforeDestroy() {
    EventBus.$emit('closePopup');
  },
};
</script>

<style lang="scss">
  .sidebarx {
    .vs-sidebar {
      max-width: 400px;

      &--items {
        overflow-y: auto;
        overflow-x: hidden !important;
      }
    }
  }
  .mapboxgl-ctrl-geocoder {
    width: 100%!important;
    max-width: unset !important;
    box-shadow: none;
    border: 1px solid #83cdb8;
  }
  .is-danger {
    border: 1px solid #ea5455 !important;
  }
  .is-true {
    border: 1px solid #28c76f !important;
  }
  .bg-color-done {
    background: #d4f4e2;
    color: #28c76f !important;
    border: 1px solid #28c76f !important;
    border-left: hidden !important;
  }

  .bg-color-error {
    background: #fbdddd;
    color: #ea5455 !important;
    border: 1px solid #ea5455 !important;
    border-left: hidden !important;
  }
</style>

<style scoped lang="scss">
  .error-text {
    font-size: 0.65rem !important;
    padding: 2px 4px 4px;
    display: block;
    margin-top: -6px;
  }

  .no-margin-span {
    margin-top: 0 !important;
  }
</style>

